import React from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/user';

interface RoleGuardProps {
  /** Roles allowed to render the wrapped element. */
  allow: UserRole[];
  /** Where to send a denied user. Defaults to the patient/dashboard landing page. */
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * RoleGuard (HA-259) — gates a route by user role. Used to keep Client-role
 * users out of practitioner-author/management routes (Forms, Templates,
 * Recipes, Meal Plans, Training, Reports, etc.) which would otherwise
 * render practitioner UI or throw an "Oops" error for them.
 *
 * Mounts inside AuthGuard, so we can assume the user is authenticated by
 * the time this renders.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ allow, redirectTo = '/Dashboard', children }) => {
  const { currentUser, isLoading } = useAuth();

  // Don't redirect while auth is still resolving — would race with the
  // AuthGuard above and could send users to /Dashboard before their
  // currentUser is hydrated.
  if (isLoading) {
    return null;
  }

  if (!currentUser || !allow.includes(currentUser.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default RoleGuard;
